'use client'

import {
  MailWarning,
} from 'lucide-react'

import {
  useJson,
} from '@/components/dashboard/credits/blocks/useJson'

import {
  WorkspaceAlertItem,
} from '@/components/alerts/BusinessMembershipExpiryAlert'

type EmailsRes = {
  includedMonthly?: {
    resend?: number
  }

  quotaMonthly?: {
    resend?: number
  }

  monthUsed?: {
    resend?: number
  }

  balance?: {
    resend?: number
  }
}

type Props = {
  visible?: boolean

  thresholdWarning?: number
  thresholdDanger?: number
}

export function useEmailQuotaUsageAlert({
  visible = true,
  thresholdWarning = 70,
  thresholdDanger = 95,
}: Props = {}): WorkspaceAlertItem | null {
  const s =
    useJson<EmailsRes>(
      '/api/credits/emails'
    )

  if (!visible) {
    return null
  }

  if (s.status !== 'ok') {
    return null
  }

  const included =
    s.data
      .includedMonthly
      ?.resend ?? 0

  const total =
    s.data
      .quotaMonthly
      ?.resend ?? 0

  const used =
    s.data
      .monthUsed
      ?.resend ?? 0

  const balance =
    s.data
      .balance
      ?.resend ?? 0

  if (total <= 0) {
    return null
  }

  const usagePct =
    Math.min(
      100,
      Math.round(
        (used / total) * 100
      )
    )

  if (
    usagePct <
    thresholdWarning
  ) {
    return null
  }

  const isExhausted =
    used >= total ||
    balance <= 0

  const isDanger =
    !isExhausted &&
    usagePct >= thresholdDanger

  const isHigh =
    !isExhausted &&
    usagePct >= 85 &&
    usagePct < thresholdDanger

  const tone =
    isExhausted
      ? 'danger'
      : isDanger
        ? 'danger'
        : isHigh
          ? 'info'
          : 'warning'

  const title =
    isExhausted
      ? 'Tus envíos de correo están detenidos'
      : isDanger
        ? 'Tus correos están por detenerse'
        : isHigh
          ? 'Te quedan pocos envíos de correo'
          : 'Te estás acercando al límite de correos'

  const message =
    isExhausted
      ? `Has agotado tus ${total} envíos disponibles este mes. Los nuevos correos permanecerán bloqueados hasta que recargues o se renueve tu cuota.`
      : isDanger
        ? `Has usado ${used} de ${total} envíos este mes. Recarga ahora y evita que tus notificaciones por correo se detengan.`
        : isHigh
          ? `Ya utilizaste ${used} de ${total} envíos este mes. Asegura tu siguiente paquete antes de quedarte sin cuota disponible.`
          : `Llevas ${used} de ${total} envíos este mes. Evita frenar las notificaciones por correo recargando con anticipación.`

  const ctaLabel =
    isExhausted
      ? 'Comprar paquete'
      : isDanger
        ? 'Comprar paquete'
        : isHigh
          ? 'Recargar emails'
          : 'Ver paquetes'

  const id =
    isExhausted
      ? 'email-quota-exhausted'
      : isDanger
        ? 'email-quota-danger'
        : isHigh
          ? 'email-quota-high'
          : 'email-quota-warning'

  return {
    id,
    tone,

    icon: (
      <MailWarning className="h-[18px] w-[18px]" />
    ),

    title,
    message,
    ctaLabel,

    onCta: () => {
      window.dispatchEvent(
        new CustomEvent(
          'hubee:buy_emails_open'
        )
      )
    },

    meta: (
      <>
        Uso:{' '}
        <span className="font-semibold tabular-nums text-foreground/80">
          {usagePct}%
        </span>

        {' • '}

        Incluidos:{' '}
        <span className="font-semibold tabular-nums text-foreground/80">
          {included}
        </span>

        {' • '}

        Restantes:{' '}
        <span className="font-semibold tabular-nums text-foreground/80">
          {balance}
        </span>
      </>
    ),
  }
}